/**
 * Admin Customer Override Audit Page
 *
 * Read-only governance dashboard over the
 * ``customer_admin_override_logs`` MongoDB collection — every entry
 * is created when a super_admin / admin / it user edits a customer
 * record that would otherwise be blocked by the post-signature
 * freeze (HTTP 403) or the self-compilation lock (HTTP 423).
 *
 * Provides GDPR Art. 16 (rectification) compliance evidence:
 *   "who changed what, when, why bypassed, from which IP"
 *
 * Super-admin gated server-side via ``require_super_admin``.
 * Linked from the Admin Navigation under "Documentazione" → audit.
 *
 * Fabrizio CR 23/06/2026 (governance phase).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, ShieldAlert, Search, ChevronDown, ChevronUp,
  FileSearch, Filter, Clock, User as UserIcon, Globe,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API } from "../config";

const LOCK_TYPES = [
  { value: "", label: "Tutti i lock" },
  { value: "signed", label: "Contratto firmato" },
  { value: "self_compilation", label: "Auto-compilazione" },
  { value: "both", label: "Entrambi i lock" },
];

const ROLE_BADGE = {
  super_admin: "bg-red-500/20 text-red-300 border-red-500/40",
  admin:       "bg-blue-500/20 text-blue-300 border-blue-500/40",
  it:          "bg-purple-500/20 text-purple-300 border-purple-500/40",
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatValue = (v) => {
  if (v === null || v === undefined) return <span className="text-gray-500 italic">(vuoto)</span>;
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 200 ? s.slice(0, 200) + "…" : s;
};

const AdminCustomerOverridesAudit = () => {
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const PAGE_SIZE = 50;

  // Filters
  const [customerIdFilter, setCustomerIdFilter] = useState("");
  const [adminEmailFilter, setAdminEmailFilter] = useState("");
  const [lockTypeFilter, setLockTypeFilter] = useState("");

  // Expansion state — entry.id → bool
  const [expanded, setExpanded] = useState({});

  const queryParams = useMemo(() => ({
    limit: PAGE_SIZE,
    skip,
    customer_id: customerIdFilter.trim() || undefined,
    admin_email: adminEmailFilter.trim() || undefined,
    lock_type: lockTypeFilter || undefined,
  }), [skip, customerIdFilter, adminEmailFilter, lockTypeFilter]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit/customer-overrides`, { params: queryParams });
      setEntries(r.data.entries || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento dell'audit log");
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [skip]);

  const applyFilters = () => {
    setSkip(0);
    // useEffect will refire because skip changed; if skip was already 0,
    // we need a manual refresh:
    if (skip === 0) fetchEntries();
  };

  const resetFilters = () => {
    setCustomerIdFilter("");
    setAdminEmailFilter("");
    setLockTypeFilter("");
    setSkip(0);
    setTimeout(fetchEntries, 0);
  };

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const canGoPrev = skip > 0;
  const canGoNext = skip + entries.length < total;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="customer-overrides-audit-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="text-gray-400 hover:text-white p-2 hover:bg-gray-800 rounded-lg transition-colors"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <ShieldAlert size={24} className="text-orange-400" />
                Audit Override Clienti
              </h1>
              <p className="text-gray-400 text-sm mt-0.5">
                Tracciato GDPR Art. 16 — modifiche admin su clienti bloccati (post-firma o in auto-compilazione)
              </p>
            </div>
          </div>
          <button
            onClick={fetchEntries}
            disabled={loading}
            className="btn-lojack-outline flex items-center gap-2 disabled:opacity-50"
            data-testid="refresh-audit-btn"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Aggiorna
          </button>
        </div>

        {/* Filters */}
        <div className="bg-[#141414] border border-gray-800 rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Filtri</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-gray-400 text-xs">Customer ID</label>
              <input
                value={customerIdFilter}
                onChange={(e) => setCustomerIdFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="uuid del cliente"
                className="w-full mt-1 px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:border-lojack-red focus:outline-none"
                data-testid="filter-customer-id"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs">Admin email</label>
              <input
                value={adminEmailFilter}
                onChange={(e) => setAdminEmailFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="es. fabrizio.guerra@telcavoip.eu"
                className="w-full mt-1 px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:border-lojack-red focus:outline-none"
                data-testid="filter-admin-email"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs">Tipo di lock bypassato</label>
              <select
                value={lockTypeFilter}
                onChange={(e) => setLockTypeFilter(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:border-lojack-red focus:outline-none"
                data-testid="filter-lock-type"
              >
                {LOCK_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2 items-end">
              <button
                onClick={applyFilters}
                className="flex-1 px-4 py-2 bg-lojack-red hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="apply-filters-btn"
              >
                <Search size={14} /> Applica
              </button>
              <button
                onClick={resetFilters}
                className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg transition-colors"
                data-testid="reset-filters-btn"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Counter */}
        <div className="flex items-center justify-between mb-4 text-sm">
          <p className="text-gray-400" data-testid="audit-count">
            {loading ? "Caricamento…" : <>
              <span className="text-white font-semibold">{total}</span> override totali
              {total > 0 && entries.length > 0 && (
                <> · visualizzati {skip + 1}-{skip + entries.length}</>
              )}
            </>}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={!canGoPrev}
              className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="prev-page-btn"
            >
              ← Precedenti
            </button>
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={!canGoNext}
              className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="next-page-btn"
            >
              Successivi →
            </button>
          </div>
        </div>

        {/* Table */}
        {!loading && entries.length === 0 ? (
          <div className="bg-[#141414] border border-gray-800 rounded-2xl p-12 text-center" data-testid="audit-empty">
            <FileSearch size={48} className="mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">Nessun override trovato con i filtri correnti.</p>
          </div>
        ) : (
          <div className="bg-[#141414] border border-gray-800 rounded-2xl overflow-hidden">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-[#0a0a0a] border-b border-gray-800">
                <tr className="text-gray-400 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Quando</th>
                  <th className="px-4 py-3 text-left">Admin</th>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-left">Lock bypassati</th>
                  <th className="px-4 py-3 text-right">Modifiche</th>
                  <th className="px-4 py-3 text-left">IP</th>
                  <th className="px-4 py-3 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {entries.map((e) => {
                  const isOpen = !!expanded[e.id];
                  return (
                    <>
                      <tr key={e.id} className="hover:bg-[#181818] transition-colors" data-testid={`audit-row-${e.id}`}>
                        <td className="px-4 py-3 align-top whitespace-nowrap text-gray-300">
                          <div className="flex items-center gap-1.5">
                            <Clock size={12} className="text-gray-500" />
                            {formatDateTime(e.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center gap-2">
                            <UserIcon size={12} className="text-gray-500" />
                            <div>
                              <div className="text-white text-xs">{e.admin_email}</div>
                              <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase border ${ROLE_BADGE[e.admin_role] || "bg-gray-500/20 text-gray-300 border-gray-500/40"}`}>
                                {e.admin_role}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-white text-xs">{e.customer_display_name}</div>
                          <div className="text-gray-500 text-[10px] font-mono">{e.customer_id?.slice(0, 8)}…</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-col gap-1">
                            {e.was_contract_signed && (
                              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-red-500/20 text-red-300 border border-red-500/40" data-testid={`lock-signed-${e.id}`}>
                                Contratto firmato
                              </span>
                            )}
                            {e.was_self_compilation_locked && (
                              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40" data-testid={`lock-self-compilation-${e.id}`}>
                                Auto-compilazione
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <span className="inline-flex items-center justify-center min-w-[24px] px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs font-bold rounded">
                            {e.num_changes}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top text-gray-400 text-xs font-mono whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Globe size={12} className="text-gray-500" />
                            {e.request_ip || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <button
                            onClick={() => toggleExpand(e.id)}
                            className="text-gray-400 hover:text-white p-1 rounded transition-colors"
                            data-testid={`toggle-diff-${e.id}`}
                          >
                            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`${e.id}-diff`} className="bg-[#0a0a0a]" data-testid={`diff-${e.id}`}>
                          <td colSpan={7} className="px-4 py-4">
                            <div className="border border-gray-800 rounded-lg overflow-hidden">
                              <div className="bg-[#181818] px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2">
                                <FileSearch size={12} />
                                Diff prima → dopo
                              </div>
                              <table className="w-full text-xs">
                                <thead className="bg-[#141414]">
                                  <tr className="text-gray-500 uppercase">
                                    <th className="px-3 py-2 text-left">Campo</th>
                                    <th className="px-3 py-2 text-left">Prima</th>
                                    <th className="px-3 py-2 text-left">Dopo</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                  {Object.entries(e.changes || {}).map(([field, ch]) => (
                                    <tr key={field}>
                                      <td className="px-3 py-2 font-mono text-white">{field}</td>
                                      <td className="px-3 py-2 text-red-300/80 font-mono break-all">{formatValue(ch.before)}</td>
                                      <td className="px-3 py-2 text-emerald-300/80 font-mono break-all">{formatValue(ch.after)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCustomerOverridesAudit;
